import React, { useEffect, useState, Fragment, useCallback } from "react";
import {
  Button,
  Col,
  Drawer,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Space,
  Modal,
  message,
  Spin,
  DatePicker,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import HeadingComponent from "../../components/HeadingComponent";
import { Voucher_Form, Voucher, vouchers_venders } from "../../utils/constants";
import CustomTable from "../../components/CustomTable";
import dayjs from "dayjs";
import { getAllPrices } from "../PackagePrice/APIs/priceApi";
import {
  addVoucher,
  deleteVoucher,
  getAllVouchers,
  updateVoucher,
} from "./APIs/voucherApi";
import { parseFirebaseDateTime } from "../../utils/helper";
import * as XLSX from "xlsx";

const { confirm } = Modal;

const Vouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [filteredVouchers, setFilteredVouchers] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editVoucher, setEditVoucher] = useState(null);

  const columns = [
    {
      title: "Code",
      dataIndex: "voucher_code",
      columnSearch: true,
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      columnSearch: true,
    },
    {
      title: "Package Price",
      dataIndex: "package_price_name",
      columnSearch: true,
    },

    {
      title: "Redemption Email",
      dataIndex: "redemption_email",
      render: (text) => {
        return text ? text : "-";
      },
    },
    {
      title: "Redemption At",
      dataIndex: "redemption_at",
      render: (redemption_at) =>
        redemption_at ? parseFirebaseDateTime(redemption_at) : "-",
    },

    {
      title: "Expires At",
      dataIndex: "expires_at",
      render: (text) => {
        return text ? dayjs(text).format("DD-MM-YYYY") : "";
      },
    },

    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <EditOutlined
            className="icons_style"
            onClick={() => handleEdit(record)}
          />

          <DeleteOutlined
            className="icons_style"
            onClick={() => showDeleteConfirm(record)}
          />
        </Space>
      ),
    },
  ];
  const showDeleteConfirm = ({ id }) => {
    confirm({
      title: `Are you sure delete this voucher?`,
      icon: <DeleteOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        handleDelete(id);
      },
    });
  };
  const handleEdit = (record) => {
    setEditVoucher(record);
    setIsEdit(true);
  };
  const handleDelete = async (id) => {
    setLoading(true);
    await deleteVoucher(id).finally(() => {
      setLoading(false);
    });
    const filtered = vouchers?.filter((x) => x.id !== id);
    setVouchers(filtered);
    setFilteredVouchers(filtered); // Update filtered vouchers as well
    message.success("Voucher deleted!");
  };

  const getVouchers = async () => {
    try {
      setLoading(true);
      let response = await getAllVouchers();
      setVouchers(response);
      setFilteredVouchers(response); // Initialize filtered vouchers with all vouchers
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getPrices = async () => {
    try {
      setLoading(true);
      let response = await getAllPrices();
      setPrices(response);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getPrices();
    getVouchers();
  }, []);

  const setVouchersCallback = useCallback((data) => {
    setVouchers((prevState) => {
      let newState;
      if (prevState.find((row) => row.id === data.id)) {
        const index = prevState.findIndex((row) => row.id === data.id);
        prevState.splice(index, 1, data);
        newState = [...prevState];
      } else {
        newState = [...prevState, data];
      }
      setFilteredVouchers(newState); // Update filtered vouchers as well
      return newState;
    });
  }, []);

  const handleVouchersDownload = async () => {
    // Use filtered vouchers instead of all vouchers
    const dataToDownload =
      filteredVouchers.length > 0 ? filteredVouchers : vouchers;

    // Step 1: Prepare the data
    const excelData = dataToDownload?.map((item) => ({
      Vendor: item.vendor,
      VoucherCode: item.voucher_code,
      PackageName: item.package_price_name,
    }));

    // Step 2: Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Step 3: Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vouchers");

    // Step 4: Generate buffer and trigger download
    const fileName =
      filteredVouchers.length !== vouchers.length
        ? "filtered_vouchers.xlsx"
        : "vouchers.xlsx";
    XLSX.writeFile(workbook, fileName);

    message.success(`Downloaded ${dataToDownload.length} voucher(s)`);
  };

  return (
    <div>
      <div className="justify_between">
        <HeadingComponent text="Vouchers" />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Button
            style={{ marginRight: 10 }}
            icon={<DownloadOutlined />}
            size="medium"
            onClick={handleVouchersDownload}
          >
            Download Voucher Codes ({filteredVouchers.length})
          </Button>
          <AddEditForm
            setVouchers={setVouchersCallback}
            isEdit={isEdit}
            editVoucher={editVoucher}
            setIsEdit={setIsEdit}
            prices={prices}
          />
        </div>
      </div>
      <br />

      <CustomTable
        dataSource={vouchers}
        columns={columns}
        loading={loading}
        onTableChange={(filteredData) => setFilteredVouchers(filteredData)}
      />
    </div>
  );
};

export default Vouchers;

const AddEditForm = ({
  isEdit = false,
  setVouchers,
  prices,
  editVoucher,
  setIsEdit,
}) => {
  const [open, setOpen] = useState(isEdit);
  const [formLoading, setFormLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (isEdit && editVoucher) {
      let safeValues = {
        ...editVoucher,
        expires_at: editVoucher?.expires_at
          ? dayjs(editVoucher?.expires_at)
          : null,
      };
      form.setFieldsValue(safeValues);
      setOpen(true);
    }
  }, [isEdit, editVoucher, form]);

  const showDrawer = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    form.resetFields();
  };

  const onSubmit = () => {
    form.validateFields().then((data) => {
      isEdit ? handleUpdate(data) : handleAdd(data);
    });
  };

  const handleAdd = async (data) => {
    const selected_price = prices?.find(
      (price) => price.stripe_price_id === data.price_id
    );
    const obj = {
      ...data,
      expires_at: data.expires_at ? data.expires_at.toISOString() : null, // Ensure proper formatting
      package_price_name: selected_price?.package_price_name || "",
      package_id: selected_price?.package_id || "",
      product_id: selected_price?.product_id || "",
    };
    setFormLoading(true);
    const response = await addVoucher(obj).finally(() => {
      setFormLoading(false);
    });

    if (response) {
      const newVoucher = { ...obj, id: response };
      setVouchers(newVoucher);
      message.success("Voucher added");
      handleClose();
    }
  };

  const handleUpdate = async (data) => {
    const selected_price = prices?.find(
      (price) => price.stripe_price_id === data.price_id
    );
    const obj = {
      ...data,
      expires_at: data.expires_at ? data.expires_at.toISOString() : null,
      id: editVoucher?.id,
      package_price_name: selected_price?.package_price_name || "",
      package_id: selected_price?.package_id || "",
      product_id: selected_price?.product_id || "",
    };
    setFormLoading(true);
    const response = await updateVoucher(obj).finally(() => {
      setFormLoading(false);
    });

    if (response) {
      const newVoucher = { ...obj, id: response };
      setVouchers(newVoucher);
      message.success("Voucher Updated");
      handleClose();
    }
  };

  return (
    <Fragment>
      <Button
        type="primary"
        onClick={showDrawer}
        icon={<PlusOutlined />}
        size="medium"
      >
        Add Voucher
      </Button>

      <Drawer
        title={isEdit ? `Edit ${Voucher}` : `Add ${Voucher}`}
        width={720}
        onClose={handleClose}
        open={open}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        <Spin spinning={formLoading}>
          <Form layout="vertical" form={form} onFinish={onSubmit}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Voucher_Form.price_id.name}
                  label={Voucher_Form.price_id.label}
                  rules={Voucher_Form.price_id.rules}
                >
                  <Select
                    options={prices?.map((price) => {
                      return {
                        label: price?.package_price_name,
                        value: price?.stripe_price_id,
                      };
                    })}
                    placeholder={Voucher_Form.price_id.placeholder}
                  />
                </Form.Item>

                <Form.Item
                  hidden
                  name={Voucher_Form.package_price_name.name}
                  label={Voucher_Form.package_price_name.label}
                >
                  <Input placeholder={Voucher_Form.voucher_code.placeholder} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Voucher_Form.voucher_code.name}
                  label={Voucher_Form.voucher_code.label}
                  rules={Voucher_Form.voucher_code.rules}
                >
                  <Input placeholder={Voucher_Form.voucher_code.placeholder} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Voucher_Form.vendor.name}
                  label={Voucher_Form.vendor.label}
                  rules={Voucher_Form.vendor.rules}
                >
                  <Select
                    options={vouchers_venders?.map((item) => {
                      return {
                        label: item,
                        value: item,
                      };
                    })}
                    placeholder={Voucher_Form.vendor.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Voucher_Form.expires_at.name}
                  label={Voucher_Form.expires_at.label}
                  rules={Voucher_Form.expires_at.rules}
                >
                  <DatePicker
                    minDate={dayjs()}
                    className="full_width"
                    min={1}
                    placeholder={Voucher_Form.expires_at.placeholder}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Flex justify="flex-end">
              <Space>
                <Button onClick={handleClose}>Cancel</Button>
                <Button htmlType="submit" type="primary">
                  Submit
                </Button>
              </Space>
            </Flex>
          </Form>
        </Spin>
      </Drawer>
    </Fragment>
  );
};
